import { CellModelStatus } from "../../../enums/CellModelStatus";
import { SquareModelFigureStatus } from "../../../enums/SquareModelFigureStatus";
import { TriangleRotationStatus } from "../../../enums/TriangleRotationStatus";
import { SquareModel } from "../../square/impl/SquareModel";
import { ISquareModel } from "../../square/ISquareModel";
import { TriangleModel } from "../../triangle/impl/TriangleModel";
import { ICellModel } from "../ICellModel";

export class CellModel implements ICellModel {
    private status: CellModelStatus;
    private figureInside: ISquareModel;
    private _eraseTriangleIndexWithAnimation: number = -1;
    public get eraseTriangleIndexWithAnimation(): number {
        return this._eraseTriangleIndexWithAnimation;
    }
    public set eraseTriangleIndexWithAnimation(value: number) {
        this._eraseTriangleIndexWithAnimation = value;
    }

    public constructor(figureInside: ISquareModel) {
        this.figureInside = figureInside;
        this.updateStatus();
    }

    private updateStatus(): void {
        let count = 0;
        for (const t of this.figureInside.Triangles) {
            if (t != null) {
                count++;
            }
        }
        switch (count) {
            case 0:
                this.status = CellModelStatus.Empty;
                break;
            case 1:
                this.status = CellModelStatus.Half;
                break;
            case 2:
                this.status = CellModelStatus.Full;
                break;
            default:
                throw new Error("Not implemented case.");
        }
    }

    public get Status(): CellModelStatus {
        return this.status;
    }

    public get FigureInside(): ISquareModel {
        return this.figureInside;
    }

    public set FigureInside(figure: ISquareModel) {
        // If cell was free -> copy contents into it
        if (this.figureInside.FigureStatus == SquareModelFigureStatus.EMPTY) {
            this.figureInside.Triangles.length = 0;
            this.figureInside.Triangles.push(...figure.Triangles);
            this.figureInside.updateStatus();
            this.updateStatus();
            return;
        }
        // If incoming figure is full
        if (figure.FigureStatus == SquareModelFigureStatus.FULL) {
            // If cell is full -> ignore all checks and clear cell
            if (this.figureInside.FigureStatus == SquareModelFigureStatus.FULL) {
                this.figureInside.Triangles.length = 0;
                this.figureInside.updateStatus();
                this.updateStatus();
                return;
            } else {
                const firstTriangle = this.figureInside.Triangles[0]; // Save it to later get it color
                this.figureInside.Triangles.length = 0;
                this.figureInside.Triangles.push(new TriangleModel(TriangleRotationStatus.getOpposite(firstTriangle.TriangleRotationStatus), figure.Triangles[0].Color));
                this.figureInside.updateStatus();
                this.updateStatus();
                return;
            }
        }
        // If cell was full and incoming figure is half -> make half figure opposite to incoming
        if (this.figureInside.FigureStatus == SquareModelFigureStatus.FULL && figure.FigureStatus == SquareModelFigureStatus.HALF) {
            const firstTriangle = this.figureInside.Triangles[0]; // Save it to later get it color
            this.figureInside.Triangles.length = 0;
            this.figureInside.Triangles.push(new TriangleModel(TriangleRotationStatus.getOpposite(figure.Triangles[0].TriangleRotationStatus), firstTriangle.Color));
            this.figureInside.updateStatus();
            this.updateStatus();
            return;
        }
        // If cell was half and incoming figure is half -> if match discard, if opposite join, else discard. 
        for (let i = 0; i < figure.Triangles.length; i++) {
            let inTriangle = figure.Triangles[i];
            for (let j = this.figureInside.Triangles.length - 1; j >= 0; j--) {
                const cellTriangle = this.figureInside.Triangles[j];
                if (inTriangle.TriangleRotationStatus == cellTriangle.TriangleRotationStatus) {
                    this.figureInside.Triangles.splice(j, 1);
                    inTriangle = null;
                    break;
                }
            }
            if (inTriangle == null) {
                continue;
            }
            this.figureInside.Triangles.push(inTriangle);
        }
        this.figureInside.updateStatus();
        this.updateStatus();
    }

    public set FigureInsideForced(figure: ISquareModel) {
        switch (figure.Triangles.length) {
            case 0:
                break;
            case 1:
                switch (this.figureInside.Triangles.length) {
                    case 0:
                        this.figureInside.Triangles.push(figure.Triangles[0]);
                        break;
                    case 1:
                        if (this.figureInside.Triangles[0].TriangleRotationStatus == figure.Triangles[0].TriangleRotationStatus) {
                            this.figureInside.Triangles[0] = figure.Triangles[0];
                        } else {
                            if (this.figureInside.Triangles[0].TriangleRotationStatus % 2 == figure.Triangles[0].TriangleRotationStatus % 2) {
                                this.figureInside.Triangles.push(figure.Triangles[0]);
                            } else {
                                this.figureInside.Triangles[0] = figure.Triangles[0];
                            }
                        }
                        break;
                    case 2:
                        let isSameStatus = false;
                        let idxThis = 0;
                        let idxInc = 0;
                        for (let i = 0; i < figure.Triangles.length; i++) {
                            for (let j = 0; j < this.figureInside.Triangles.length; j++) {
                                const triangle = this.figureInside.Triangles[j];
                                if (triangle.TriangleRotationStatus == figure.Triangles[i].TriangleRotationStatus) {
                                    isSameStatus = true;
                                    idxThis = j;
                                    idxInc = i;
                                }
                            }
                        }
                        if (isSameStatus) {
                            this.figureInside.Triangles[idxThis] = figure.Triangles[idxInc];
                        } else {
                            this.figureInside.Triangles[0] = figure.Triangles[0];
                            this.figureInside.Triangles.pop();
                        }
                        break;
                }
                this.figureInside.updateStatus();
                break;
            case 2:
                this.figureInside.Triangles[0] = figure.Triangles[0];
                this.figureInside.Triangles[1] = figure.Triangles[1];
                this.figureInside.updateStatus();
                break;
        }
        this.updateStatus();
    }

    public eraseByBooster(): void {
        this.figureInside = new SquareModel([]);
        this.updateStatus();
    }

    public eraseTriangleByIndex(index: number): void {
        this._eraseTriangleIndexWithAnimation = index;
        const triangles = this.figureInside.Triangles.filter((_, i) => i != index);
        this.figureInside = new SquareModel(triangles);
        this.updateStatus();
    }

    public tryCombine(figure: ISquareModel): boolean {
        if (figure.FigureStatus == SquareModelFigureStatus.EMPTY || figure.FigureStatus == SquareModelFigureStatus.FULL) {
            return true;
        }
        switch (this.status) {
            case CellModelStatus.Empty:
            case CellModelStatus.Full:
                return true;
            case CellModelStatus.Half:
                for (let i = 0; i < this.figureInside.Triangles.length; i++) {
                    for (let j = 0; j < figure.Triangles.length; j++) {
                        if (figure.Triangles[j].TriangleRotationStatus % 2 != this.figureInside.Triangles[i].TriangleRotationStatus % 2) {
                            return false;
                        }
                    }
                }
        }
        return true;
    }
}